import { ProjectPeriodModel } from "app/models/project.model";
import { ProjectPeriodSegment, getProjectPeriodSegments, normalizeProjectPeriod } from "./project-periods.core";

type ProjectPeriodRange = {
    start: number;
    end: number;
};

const PROJECT_PERIOD_SLOT_MINUTES = 30;
const PROJECT_PERIOD_SLOTS_PER_DAY = (24 * 60) / PROJECT_PERIOD_SLOT_MINUTES;

function mergeContiguousProjectPeriodSegments(segments: ProjectPeriodSegment[]): ProjectPeriodSegment[] {
    const ordered = [...segments].filter((segment) => segment.end > segment.start).sort((left, right) => left.start - right.start);
    const merged: ProjectPeriodSegment[] = [];

    for (const segment of ordered) {
        const previous = merged[merged.length - 1];
        if (previous && previous.periodName === segment.periodName && previous.end === segment.start) {
            previous.end = segment.end;
            continue;
        }

        merged.push({ ...segment });
    }

    return merged;
}

function minutesToTime(totalMinutes: number) {
    const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
    return {
        hour: Math.floor(normalized / 60),
        minute: normalized % 60,
    };
}

function buildProjectPeriodsFromSegments(segments: ProjectPeriodSegment[]): ProjectPeriodModel[] {
    const mergedSegments = mergeContiguousProjectPeriodSegments(segments);
    const groupedRanges = new Map<string, ProjectPeriodRange[]>();
    const order = new Map<string, number>();

    for (const segment of mergedSegments) {
        if (!groupedRanges.has(segment.periodName)) {
            groupedRanges.set(segment.periodName, []);
            order.set(segment.periodName, segment.start);
        }

        groupedRanges.get(segment.periodName)?.push({
            start: segment.start,
            end: segment.end,
        });
    }

    return Array.from(groupedRanges.entries())
        .sort(
            ([leftName], [rightName]) =>
                (order.get(leftName) ?? Number.POSITIVE_INFINITY) - (order.get(rightName) ?? Number.POSITIVE_INFINITY),
        )
        .map(([name, ranges]) => {
            const normalizedRanges = [...ranges].sort((left, right) => left.start - right.start);

            return {
                name,
                intervals: normalizedRanges.map((range) => {
                    if (range.start === 0 && range.end === 24 * 60) {
                        return {
                            startTime: minutesToTime(0),
                            endTime: minutesToTime(0),
                        };
                    }

                    return {
                        startTime: minutesToTime(range.start),
                        endTime: minutesToTime(range.end === 24 * 60 ? 0 : range.end),
                    };
                }),
            };
        });
}

function subtractProjectPeriodSegments(
    sourceSegments: ProjectPeriodSegment[],
    removedSegments: ProjectPeriodSegment[],
): ProjectPeriodSegment[] {
    const removals = [...removedSegments].filter((segment) => segment.end > segment.start).sort((left, right) => left.start - right.start);

    return sourceSegments.flatMap((sourceSegment) => {
        let remainingSegments = [{ ...sourceSegment }];

        for (const removedSegment of removals) {
            remainingSegments = remainingSegments.flatMap((candidate) => {
                const overlapStart = Math.max(candidate.start, removedSegment.start);
                const overlapEnd = Math.min(candidate.end, removedSegment.end);

                if (overlapStart >= overlapEnd) {
                    return [candidate];
                }

                const nextSegments: ProjectPeriodSegment[] = [];
                if (candidate.start < overlapStart) {
                    nextSegments.push({
                        ...candidate,
                        end: overlapStart,
                    });
                }
                if (overlapEnd < candidate.end) {
                    nextSegments.push({
                        ...candidate,
                        start: overlapEnd,
                    });
                }

                return nextSegments;
            });
        }

        return remainingSegments;
    });
}

function findPreviousProjectPeriodSegment(segments: ProjectPeriodSegment[], minute: number): ProjectPeriodSegment | null {
    if (!segments.length) {
        return null;
    }

    let previous: ProjectPeriodSegment | null = null;
    for (const segment of segments) {
        if (segment.start < minute) {
            previous = segment;
            continue;
        }

        break;
    }

    return previous ?? segments[segments.length - 1];
}

function buildProjectPeriodSlotOwners(periods: ProjectPeriodModel[]): Array<string | null> {
    const owners = Array<string | null>(PROJECT_PERIOD_SLOTS_PER_DAY).fill(null);

    periods.flatMap(getProjectPeriodSegments).forEach((segment) => {
        const startSlot = Math.floor(segment.start / PROJECT_PERIOD_SLOT_MINUTES);
        const endSlot = Math.ceil(segment.end / PROJECT_PERIOD_SLOT_MINUTES);

        for (let slot = startSlot; slot < endSlot; slot += 1) {
            owners[slot] = segment.periodName;
        }
    });

    return owners;
}

function findNeighborSlotOwner(owners: Array<string | null>, startIndex: number, direction: -1 | 1): string | null {
    for (let offset = 1; offset <= owners.length; offset += 1) {
        const index = (startIndex + direction * offset + owners.length) % owners.length;
        const owner = owners[index];
        if (owner) {
            return owner;
        }
    }

    return null;
}

function buildProjectPeriodSegmentsFromSlotOwners(owners: Array<string | null>): ProjectPeriodSegment[] {
    const normalizedOwners = owners.map((owner) => owner ?? "Day");
    const firstOwner = normalizedOwners[0];

    if (normalizedOwners.every((owner) => owner === firstOwner)) {
        return [{ start: 0, end: 24 * 60, periodName: firstOwner }];
    }

    const segments: ProjectPeriodSegment[] = [];
    let startSlot = 0;

    for (let slot = 1; slot <= normalizedOwners.length; slot += 1) {
        const currentOwner = normalizedOwners[slot % normalizedOwners.length];
        const previousOwner = normalizedOwners[(slot - 1) % normalizedOwners.length];

        if (slot < normalizedOwners.length && currentOwner === previousOwner) {
            continue;
        }

        segments.push({
            start: startSlot * PROJECT_PERIOD_SLOT_MINUTES,
            end: slot * PROJECT_PERIOD_SLOT_MINUTES,
            periodName: previousOwner,
        });
        startSlot = slot;
    }

    return segments;
}

export function replaceProjectPeriodCoverage(periods: ProjectPeriodModel[], nextPeriod: ProjectPeriodModel): ProjectPeriodModel[] {
    const normalizedNextPeriod = normalizeProjectPeriod(nextPeriod);
    const nextSegments = getProjectPeriodSegments(normalizedNextPeriod);
    const baseSegments = periods.filter((period) => period.name !== normalizedNextPeriod.name).flatMap(getProjectPeriodSegments);
    const survivingSegments = subtractProjectPeriodSegments(baseSegments, nextSegments);

    return buildProjectPeriodsFromSegments([...survivingSegments, ...nextSegments]);
}

export function rebalanceEditedProjectPeriodCoverage(
    periods: ProjectPeriodModel[],
    previousPeriodName: string,
    nextPeriod: ProjectPeriodModel,
): ProjectPeriodModel[] {
    const normalizedNextPeriod = normalizeProjectPeriod(nextPeriod);
    const slotOwners = buildProjectPeriodSlotOwners(periods.filter((period) => period.name !== previousPeriodName));

    getProjectPeriodSegments(normalizedNextPeriod).forEach((segment) => {
        const startSlot = Math.floor(segment.start / PROJECT_PERIOD_SLOT_MINUTES);
        const endSlot = Math.ceil(segment.end / PROJECT_PERIOD_SLOT_MINUTES);

        for (let slot = startSlot; slot < endSlot; slot += 1) {
            slotOwners[slot] = normalizedNextPeriod.name;
        }
    });

    for (let slot = 0; slot < slotOwners.length; slot += 1) {
        if (slotOwners[slot]) {
            continue;
        }

        const leftOwner = findNeighborSlotOwner(slotOwners, slot, -1);
        const rightOwner = findNeighborSlotOwner(slotOwners, slot, 1);

        if (leftOwner && leftOwner !== normalizedNextPeriod.name) {
            slotOwners[slot] = leftOwner;
            continue;
        }

        if (rightOwner && rightOwner !== normalizedNextPeriod.name) {
            slotOwners[slot] = rightOwner;
            continue;
        }

        slotOwners[slot] = leftOwner ?? rightOwner ?? normalizedNextPeriod.name;
    }

    return buildProjectPeriodsFromSegments(buildProjectPeriodSegmentsFromSlotOwners(slotOwners));
}

export function removeProjectPeriodCoverage(periods: ProjectPeriodModel[], periodName: string): ProjectPeriodModel[] {
    const periodsWithoutTarget = periods.filter((period) => period.name !== periodName).map((period) => normalizeProjectPeriod(period));
    const validPeriodsWithoutTarget = periodsWithoutTarget.filter((period) => getProjectPeriodSegments(period).length > 0);
    const remainingSegments = periods
        .filter((period) => period.name !== periodName)
        .flatMap(getProjectPeriodSegments)
        .sort((left, right) => left.start - right.start);
    if (!remainingSegments.length) {
        return [];
    }

    const removedSegments = periods.filter((period) => period.name === periodName).flatMap(getProjectPeriodSegments);
    if (!removedSegments.length) {
        return validPeriodsWithoutTarget;
    }

    const fillerSegments = removedSegments.map((segment) => ({
        start: segment.start,
        end: segment.end,
        periodName: findPreviousProjectPeriodSegment(remainingSegments, segment.start)?.periodName ?? "Day",
    }));

    return buildProjectPeriodsFromSegments([...remainingSegments, ...fillerSegments]);
}
